import { useEffect, useMemo, useRef } from 'react';
import type { DealerFormType } from '@/app/api/lms/type';
import type { ProvinceWithDistrictsType } from '@/app/types/form';
import { compareLocaleString } from '@/app/utils/sorts';
import type { DistrictType } from '@/services/contentstack/models/district';
interface DistrictWithDealersType extends DistrictType {
  dealers?: DealerFormType[];
}
const getDealersFromDistricts = (districts: DistrictWithDealersType[] | undefined) => {
  if (!districts || !Array.isArray(districts)) {
    return [];
  }
  const flattensDealers = districts.flatMap(o => o.dealers || []);
  const sortedDealers = flattensDealers.sort((a, b) => compareLocaleString(a.name_th, b.name_th, 'th'));
  const uniqueDealers = Array.from(new Map(sortedDealers.map(o => [o.branchCode, o])).values());
  return uniqueDealers;
};
interface UseDealersFromProvincesOptions {
  values: {
    province?: string | null;
    district?: string | null;
    dealer?: string | null;
  };
  setValue: (field: string, value: string) => void;
}

/**
 * Custom hook for managing dealer selection based on provinces and districts.
 * Handles cascading selection (province -> district -> dealer) and validates
 * form data according to LMS form schema.
 *
 * @param provinces - Array of provinces with their districts and dealers
 * @param options - Optional configuration including initial values
 * @returns Object containing state values, setters, and helper functions:
 *   - provinceId: Currently selected province UID
 *   - setProvinceId: Function to set province selection
 *   - provinces: Original provinces array
 *   - districtId: Currently selected district UID
 *   - setDistrictId: Function to set district selection
 *   - districts: Available districts for selected province
 *   - selectedDealer: Currently selected dealer object
 *   - setSelectedDealer: Function to set dealer selection
 *   - dealers: Available dealers based on province/district selection
 *   - reset: Function to reset all selections to initial state
 */
export const useDealersFromProvinces = (provinces: ProvinceWithDistrictsType[], {
  values,
  setValue
}: UseDealersFromProvincesOptions) => {
  const prevDealersRef = useRef<DealerFormType[]>([]);
  const provinceId = values.province || null;
  const districtId = values.district || null;
  const {
    districts,
    dealers
  } = useMemo(() => {
    const selectedProvince = provinceId ? provinces.find(p => p.uid === provinceId) : null;
    const districts = selectedProvince?.districts || [];
    let dealers: DealerFormType[] = [];
    if (selectedProvince) {
      if (districtId) {
        const selectedDistrict = districts.find(d => d.uid === districtId);
        dealers = getDealersFromDistricts(selectedDistrict ? [selectedDistrict] : []);
      } else {
        dealers = getDealersFromDistricts(selectedProvince.districts);
      }
    }
    return {
      districts,
      dealers
    };
  }, [provinceId, districtId, provinces]);
  const selectedDealer = useMemo(() => values.dealer ? dealers.find(d => d.branchCode === values.dealer) || null : null, [values.dealer, dealers]);
  useEffect(() => {
    if (dealers.length === 1 && values.dealer !== dealers[0].branchCode) {
      setValue('dealer', dealers[0].branchCode);
      prevDealersRef.current = dealers;
      return;
    }
    const prevDealers = prevDealersRef.current;
    const dealersChanged = dealers.length !== prevDealers.length || dealers.some((d, i) => d.branchCode !== prevDealers[i]?.branchCode);
    if (dealersChanged) {
      prevDealersRef.current = dealers;
      if (dealers.length === 0 && values.dealer) {
        setValue('dealer', '');
      } else if (dealers.length > 1) {
        const isDealerValid = values.dealer && dealers.some(d => d.branchCode === values.dealer);
        if (!isDealerValid) {
          setValue('dealer', '');
        }
      }
    }

    // Clear dealer when district is deselected but multiple dealers exist
    if (!districtId && dealers.length > 1 && values.dealer !== '') {
      setValue('dealer', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealers, districtId]);
  const handleProvinceChange = (newProvinceId: string) => {
    setValue('province', newProvinceId);
    setValue('district', '');
    setValue('dealer', '');
  };
  const handleDistrictChange = (newDistrictId: string) => {
    setValue('district', newDistrictId);
  };
  const handleDealerChange = (dealerBranchCode: string) => {
    setValue('dealer', dealerBranchCode);
  };
  const reset = () => {
    setValue('province', '');
    setValue('district', '');
    setValue('dealer', '');
  };
  return {
    provinceId,
    provinces,
    districtId,
    districts,
    selectedDealer,
    dealers,
    handleProvinceChange,
    handleDistrictChange,
    handleDealerChange,
    reset
  };
};